import { useEffect, useRef, useState } from 'react'
import { api, type DocumentSummary, type VehicleDetail } from '../api/client'
import { buildVehicleShareText } from './shareText'
import { EmailReport } from './EmailReport'
import './ShareVehicle.css'

type Outcome = { kind: 'ok' | 'error'; message: string } | null

/**
 * Sharing one vehicle's compliance position.
 *
 * The text half is easy. The certificates are not, and the honest answer differs
 * by platform, so this does not pretend otherwise:
 *
 *   WhatsApp via a wa.me link carries TEXT ONLY. There is no parameter that
 *   attaches a file — it is a URL scheme, not an upload.
 *
 *   mailto: cannot attach anything either. That is a limit of the scheme — which
 *   is why Email no longer uses one. It hands off to EmailReport, which sends
 *   from the server with the report attached as a PDF and records that it
 *   happened. The scheme's limit was never the real problem; the real problem was
 *   that nobody could tell whether a report had actually gone out.
 *
 *   The Web Share API's file support is the one route that genuinely hands a PDF
 *   to WhatsApp, and it exists on Android Chrome and iOS Safari — which is where
 *   this fleet is actually worked. On a desktop browser it is mostly absent.
 *
 * So: attach the files where the platform can, and where it cannot, download them
 * and say plainly that they must be attached by hand. What this deliberately does
 * NOT do is mint a public link to a document. Content is streamed through the API
 * so that authorisation is decided per request; a shareable URL that skips that
 * is a document leak wearing a convenience feature, and it is a decision to take
 * deliberately rather than as a side effect of a share button.
 */
export function ShareVehicle({
  vehicle,
  documents,
  onClose,
}: {
  vehicle: VehicleDetail
  documents: DocumentSummary[]
  onClose: () => void
}) {
  const [includeLink, setIncludeLink] = useState(true)
  const [emailing, setEmailing] = useState(false)
  const [outcome, setOutcome] = useState<Outcome>(null)
  const [busy, setBusy] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // A link back to this page. Safe to send: it opens the app, which authenticates
  // every request — it is not a way around signing in.
  const link = `${window.location.origin}/vehicles/${vehicle.registration}`

  const text = buildVehicleShareText(vehicle, {
    link: includeLink ? link : undefined,
    documentCount: documents.length,
  })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  useEffect(() => {
    panelRef.current?.querySelector('button')?.focus()
  }, [])

  // Level 2 of the Web Share API. Probed with a token File rather than assumed:
  // several browsers expose navigator.share and refuse files.
  const canShareFiles =
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({
      files: [new File(['probe'], 'probe.pdf', { type: 'application/pdf' })],
    })

  const canShareText = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  async function copyText() {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text)
      else throw new Error('clipboard unavailable')

      setOutcome({ kind: 'ok', message: 'Details copied. Paste them wherever you need.' })
    } catch {
      setOutcome({
        kind: 'error',
        message: 'Could not copy — select the text below and copy it by hand.',
      })
    }
  }

  function openWhatsApp() {
    // wa.me rather than the whatsapp:// scheme: it resolves to the app on a phone
    // and to WhatsApp Web on a desktop, instead of failing on whichever one the
    // machine does not have.
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener')
  }

  async function shareNative(withFiles: boolean) {
    setBusy(true)
    setOutcome(null)

    try {
      if (!withFiles) {
        await navigator.share({ title: vehicle.registration ?? 'Vehicle', text })
        return
      }

      const files = await fetchDocuments(vehicle.vehicleId!, documents)

      if (files.length === 0) {
        setOutcome({ kind: 'error', message: 'None of the certificates could be downloaded.' })
        return
      }

      if (!navigator.canShare({ files })) {
        setOutcome({
          kind: 'error',
          message:
            'This browser will not share these files. Use “Download certificates” and attach them yourself.',
        })
        return
      }

      await navigator.share({ title: vehicle.registration ?? 'Vehicle', text, files })
    } catch (error) {
      // The user dismissing the share sheet arrives here as AbortError. That is
      // not a failure and must not be reported as one.
      if ((error as Error)?.name === 'AbortError') return

      setOutcome({
        kind: 'error',
        message:
          'Sharing was refused by the browser. Some browsers only allow it immediately on tap — ' +
          'try again, or download the certificates and attach them yourself.',
      })
    } finally {
      setBusy(false)
    }
  }

  /**
   * Downloads each certificate through the API.
   *
   * The everywhere-fallback. It is more steps than an automatic attachment, and
   * it is the only approach that works in every browser without weakening how
   * documents are authorised.
   */
  async function downloadDocuments() {
    for (const doc of documents) {
      const anchor = document.createElement('a')
      anchor.href = api.documentContentUrl(vehicle.vehicleId!, doc.complianceDocumentId!, true)
      anchor.download = doc.fileName ?? 'certificate'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()

      // Browsers throttle or drop rapid successive downloads; a short gap between
      // them is the difference between four files and one.
      await new Promise((resolve) => setTimeout(resolve, 350))
    }

    setOutcome({
      kind: 'ok',
      message: `${documents.length} file${documents.length > 1 ? 's' : ''} downloaded. Attach them to your message.`,
    })
  }

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div className="modal modal--share" role="dialog" aria-modal="true" aria-label="Share vehicle">
        <header className="modal__head">
          <h2>Share {vehicle.registration}</h2>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="modal__body" ref={panelRef}>
          <div className="share__row">
            <button type="button" className="share__btn share__btn--wa" onClick={openWhatsApp}>
              WhatsApp
            </button>
            <button
              type="button"
              className={`share__btn${emailing ? ' share__btn--on' : ''}`}
              aria-expanded={emailing}
              onClick={() => setEmailing((v) => !v)}
            >
              Email
            </button>
            <button type="button" className="share__btn" onClick={copyText}>
              Copy details
            </button>
            {canShareText && (
              <button
                type="button"
                className="share__btn"
                disabled={busy}
                onClick={() => void shareNative(false)}
              >
                Share…
              </button>
            )}
          </div>

          {/*
            Sent by the server, not handed to a mail client. That is what lets it
            carry the PDF and be recorded — a mailto could do neither.
          */}
          {emailing && (
            <section className="share__email">
              <EmailReport
                vehicleIds={[vehicle.vehicleId!]}
                label={`A report for ${vehicle.registration}`}
                onDone={() => setEmailing(false)}
              />
            </section>
          )}

          <label className="share__opt">
            <input
              type="checkbox"
              checked={includeLink}
              onChange={(e) => setIncludeLink(e.target.checked)}
            />
            <span>
              Include a link to this page. It opens Prarambh and still asks the recipient to
              sign in — it is not a way around that.
            </span>
          </label>

          {/* ---------------------------------------------------------- */}
          {/* Certificates                                                */}
          {/* ---------------------------------------------------------- */}
          <section className="share__docs">
            <h3 className="eyebrow">Certificates</h3>

            {documents.length === 0 ? (
              <p className="share__note">Nothing is attached to this vehicle yet.</p>
            ) : (
              <>
                <p className="share__note">
                  {documents.length} on file. <b>WhatsApp links and email drafts carry text
                  only</b> — neither can attach a file from a web page.
                </p>

                {canShareFiles ? (
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={busy}
                    onClick={() => void shareNative(true)}
                  >
                    {busy ? 'Preparing…' : `Share with ${documents.length} file${documents.length > 1 ? 's' : ''}`}
                  </button>
                ) : (
                  <p className="share__note share__note--warn">
                    This browser cannot hand files to another app. Download them and attach
                    them yourself — on a phone, the same button offers WhatsApp directly.
                  </p>
                )}

                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => void downloadDocuments()}
                >
                  Download certificate{documents.length > 1 ? 's' : ''}
                </button>
              </>
            )}
          </section>

          {outcome && (
            <p
              className={outcome.kind === 'ok' ? 'editor__note' : 'editor__conflict'}
              role="status"
            >
              {outcome.message}
            </p>
          )}

          <details className="share__preview">
            <summary>What will be sent</summary>
            {/* readOnly rather than disabled: the user can still select and copy
                it by hand, which is the fallback when the clipboard is refused. */}
            <textarea className="share__text" value={text} readOnly rows={14} />
          </details>
        </div>
      </div>
    </>
  )
}

/**
 * Pulls each document's bytes so they can be handed to the share sheet.
 *
 * Fetched through the API with credentials, exactly like the viewer does, so
 * authorisation is unchanged — the files are only ever readable by someone who
 * could already open them.
 *
 * A file that fails is skipped rather than aborting the batch: three of four
 * certificates is a better outcome than none, and the caller reports the count.
 */
async function fetchDocuments(vehicleId: number, documents: DocumentSummary[]): Promise<File[]> {
  const files: File[] = []

  for (const doc of documents) {
    try {
      const response = await fetch(
        api.documentContentUrl(vehicleId, doc.complianceDocumentId!),
        { credentials: 'same-origin' },
      )
      if (!response.ok) continue

      const blob = await response.blob()
      files.push(
        new File([blob], doc.fileName ?? 'certificate', {
          type: doc.contentType ?? blob.type,
        }),
      )
    } catch {
      // Skipped deliberately — see the note above.
    }
  }

  return files
}
